import { Component } from "react";
import { inject, observer } from "mobx-react";
import type { Theme } from "@mui/material/styles";

import { isValue, replaceUndefinedWithNulls } from "@core/utils";

import { HighchartsBase } from "../../ui/analytics/charts";
import withTheme from "../../withTheme";
import { formatCompareDifference, formatTooltipValue } from "./charts/utils";

/** A Highcharts series options bag: `data`, plus whatever else the call site sets. */
type CompareSeries = {
  data: Array<number | null | undefined>;
  [option: string]: unknown;
};

export type ColumnSeriesCompareProps = {
  /** Rendered as the column series. */
  series1: CompareSeries;
  /** Rendered as the line series. */
  series2: CompareSeries;
  categories: Array<string | number>;
  yUnit: string;
  width?: number;
  /** Shortens the chart from 240px to 180px. */
  dense?: boolean;
  scaleYAxis?: boolean;
  /** Injected by `withTheme` — call sites do not pass this. */
  theme?: Theme;
};

class GraphColCompare extends Component<ColumnSeriesCompareProps> {
  render() {
    const {
      series1,
      series2,
      yUnit,
      width,
      categories,
      theme,
      dense,
      scaleYAxis = false,
    } = this.props;
    return (
      <HighchartsBase
        yAxisScale={scaleYAxis}
        xAxis={{
          gridLineWidth: 0,
          lineWidth: 0,
          categories,
          labels: {
            formatter() {
              return categories[this.pos];
            },
          },
        }}
        yUnit={yUnit}
        yAxisOptions={{
          title: {
            text: "",
          },
          gridLineWidth: 0,
          lineWidth: 0,
        }}
        chart={{
          height: dense ? 180 : 240,
          marginTop: 20,
          type: "column",
          zoomType: "",
          width,
          backgroundColor: "white",
        }}
        seriesOptions={{ marker: { enabled: false } }}
        tooltip={{
          shared: true,
          formatter() {
            const axisMax =
              this.points?.[0]?.series?.yAxis?.max ??
              this.points?.[0]?.series?.chart?.yAxis?.[0]?.max;
            let ttip = ``;
            const [point0, point1] = this.points || [];
            const firstPoint = point0 ? formatTooltipValue(point0.y, yUnit, axisMax) : null;
            const secondPoint = point1 ? formatTooltipValue(point1.y, yUnit, axisMax) : null;

            if (point0) {
              ttip += `<span style="color:${point0.color};">&#9679;&nbsp;${point0.series.userOptions.label} ${firstPoint} </span><br/>`;
            }

            if (point1) {
              ttip += `<span style="color:${point1.color};">&#9679;&nbsp;${point1.series.userOptions.label} ${secondPoint} </span><br/>`;
            }

            if (point0 && point1 && isValue(point0.y) && isValue(point1.y)) {
              ttip += `<span>&nbsp;&nbsp;&nbsp;Difference ${formatCompareDifference(point0.y, point1.y, yUnit, axisMax)}</span>`;
            }

            return ttip;
          },
        }}
        legend={{ enabled: false }}
        disableBoost
        plotOptions={{
          series: {
            pointWidth: 35,
          },
        }}
        series={[
          {
            ...series1,
            data: replaceUndefinedWithNulls(series1.data),
            type: "column",
            color: theme.palette.graph.blue,
            showInLegend: true,
            pointWidth: 10,
          },
          {
            ...series2,
            data: replaceUndefinedWithNulls(series2.data),
            type: "line",
            showInLegend: true,
            color: theme.palette.secondary.main,
          },
        ]}
      />
    );
  }
}

export default inject("rootStore")(withTheme(observer(GraphColCompare)));
